import { useNavigate } from "@tanstack/react-router";

import { Tabs } from "@/components/ui/tabs";

import { MaintainObjects } from "./maintain-objects";
import { NorthStar } from "./north-star";
import { PageMap } from "./page-map";
import { RuntimeFlow } from "./runtime-flow";
import { STAGES, type StageKey } from "./shared";

/**
 * 链路总览四个页签（11-02 加「维护对象」放第二个，对象 × 三层，每格链到对应页面；10-21 第三轮原「一二级架构」并进页面地图；10-25 加「问题与主张」放第一个、作默认）。查询参数 `view` 记住当前页签，跳走再回来不丢：
 * ⓪ 问题与主张（`north-star.tsx`）：核心问题、三组「问题 → 主张」、目标规模化，正本是基线 01 第零节；
 * ① 页面地图（`page-map.tsx`）：按菜单一级分组、按层排；选中一页在右侧详情区看它的主要能力、读了谁、被谁读；
 * ② 运行时流程（`runtime-flow.tsx`）：一轮从客户原话到回复的七步，底部可展开「对象 × 七步」对照表。
 * 旧链接 `?view=arch`（一二级架构）落到页面地图；不带 `view` 落到问题与主张。`step` ＝ 运行时流程里高亮哪一步（详情区点 ①～⑦ 带过来）。
 * 节点 → 页面、菜单分组、页面依赖与能力全部读页面注册表（`../page-registry.json`），这里不写映射。
 */

export type OverviewView = "north" | "objects" | "runtime" | "map";

const VIEWS: { key: OverviewView; label: string }[] = [
	{ key: "north", label: "问题与主张" },
	{ key: "objects", label: "维护对象" },
	{ key: "map", label: "页面地图" },
	{ key: "runtime", label: "运行时流程" },
];

/** 不带值落到问题与主张；旧的 arch 落到页面地图；其余不认识的值落到问题与主张 */
export function parseOverviewView(v: string | undefined): OverviewView {
	if (v === "runtime" || v === "map" || v === "objects") return v;
	if (v === "arch") return "map";
	return "north";
}

export function parseOverviewStep(v: string | undefined): StageKey | null {
	return STAGES.find((s) => s.key === v)?.key ?? null;
}

export function OverviewTab({ view, step }: { view: OverviewView; step: StageKey | null }) {
	const navigate = useNavigate();
	return (
		<div className="space-y-4" data-testid="plan-block-overview">
			<Tabs
				items={VIEWS}
				active={view}
				onChange={(v) =>
					navigate({
						to: "/plan-lab",
						search: v === "north" ? {} : { view: v },
						hash: "overview",
						replace: true,
					})
				}
			/>
			{view === "north" ? <NorthStar /> : null}
			{view === "objects" ? <MaintainObjects /> : null}
			{view === "runtime" ? <RuntimeFlow focus={step} /> : null}
			{view === "map" ? <PageMap /> : null}
		</div>
	);
}
